import {useMemo} from 'react';

import {useI18n} from '@udemy/i18n';
import {
    useVoucherPricing,
    useExamsByIds,
    VoucherPoolProductType,
} from '@udemy/react-certification-vouchers';
import {CertificationVoucherBuyable, ShoppingItem as UdemyShoppingItem} from '@udemy/shopping';

import {type BundleType, useBundleForCourseUpsell} from 'src/hooks/use-bundle-for-course-upsell';
import {useCoursePrice} from 'src/hooks/use-course-price';

import {
    buildShoppingItemFromGqlCourse,
    buildVoucherShoppingItemFromPool,
    isEffectivelyFree,
} from '../certification-upsell-shopping-item-builders';
import {CertificationBundle} from '../certification-upsell.types';

interface ResolvedUpsell {
    otherCourseId: number;
    voucherItem: UdemyShoppingItem;
}

export interface UseCertificationCourseUpsellParams {
    item: UdemyShoppingItem;
    cartCourseIds?: Set<number>;
    cartVoucherOfferIds?: Set<string>;
    isExpansionEnabled?: boolean;
}

export interface UseCertificationCourseUpsellResult {
    bundleType: BundleType;
    resolved: ResolvedUpsell | null;
    otherCourseItem: UdemyShoppingItem | null;
    showSuggestedCourse: boolean;
    showVoucher: boolean;
    hasContent: boolean;
    voucherOfferId: string | undefined;
    isLoading: boolean;
    wouldShowGenericBundle: boolean;
}

export function useCertificationCourseUpsell({
    item,
    cartCourseIds,
    cartVoucherOfferIds,
    isExpansionEnabled = false,
}: UseCertificationCourseUpsellParams): UseCertificationCourseUpsellResult {
    const {gettext, interpolate} = useI18n();
    const courseId = item.buyable.id;

    const {
        bundle,
        bundleLoading,
        bundleType: rawBundleType,
        relatedTopicId,
        examInTopic,
        otherCourseInBundle,
    } = useBundleForCourseUpsell(courseId);

    // Generic bundle upsell is only shown when the expansion flag is enabled.
    const bundleType: BundleType =
        rawBundleType === 'generic' && !isExpansionEnabled ? null : rawBundleType;
    const {course: otherCourse, isLoading: otherCourseLoading} = otherCourseInBundle;

    const examIsLoading = Boolean(relatedTopicId) && examInTopic.isLoading;

    const voucherPool =
        !examIsLoading && relatedTopicId
            ? examInTopic.voucherPool ?? examInTopic.defaultPool
            : null;

    const voucherPricing = useVoucherPricing({
        voucherPool: examInTopic.voucherPool,
        retakeInsurancePool: examInTopic.retakeInsurancePool,
        defaultPool: examInTopic.defaultPool,
        hasBothPoolsAvailable: examInTopic.hasBothPoolsAvailable,
        isExamLoading: examIsLoading,
    });

    const {selectedPrice, retakeInsurancePrice} = voucherPricing;

    const cartVoucherOfferIdList = useMemo(
        () => (cartVoucherOfferIds?.size ? Array.from(cartVoucherOfferIds) : []),
        [cartVoucherOfferIds],
    );
    const {exams: cartExams, isLoading: cartExamsLoading} = useExamsByIds(cartVoucherOfferIdList);

    const voucherExamId = voucherPool?.examId;
    const voucherInCartForBundleTopic = useMemo(() => {
        if (!relatedTopicId || cartExamsLoading) return false;
        return cartExams.some((exam) => exam.topic?.id === relatedTopicId);
    }, [relatedTopicId, cartExams, cartExamsLoading]);
    const voucherAlreadyInCart = Boolean(
        (voucherExamId && cartVoucherOfferIds?.has(voucherExamId)) || voucherInCartForBundleTopic,
    );

    const resolved = useMemo((): ResolvedUpsell | null => {
        if (!voucherPool) return null;

        const isUdemyIssuedRetake =
            examInTopic.retakeInsurancePool?.productType ===
            VoucherPoolProductType.UdemyIssuedRetakeInsurance;
        const titleTemplate = retakeInsurancePrice
            ? isUdemyIssuedRetake
                ? gettext('%(title)s Exam Voucher + Second Chance')
                : gettext('%(title)s Exam Voucher + Retake')
            : gettext('%(title)s Exam Voucher');
        const voucherTitle = interpolate(titleTemplate, {title: voucherPool.title}, true);
        const voucherItem = buildVoucherShoppingItemFromPool(
            {
                ...voucherPool,
                title: voucherTitle,
                imageUrl: voucherPool.imageUrl ?? '',
                expirationThresholdInMonths: voucherPool.expirationThresholdInMonths ?? 12,
                issuerName: examInTopic.issuerName ?? undefined,
            },
            retakeInsurancePrice ?? selectedPrice ?? undefined,
        );

        if (!voucherItem) return null;

        if (bundle) {
            const other = (bundle as unknown as CertificationBundle).contents.find(
                (c) => c.id !== courseId,
            );
            if (other) {
                return {otherCourseId: other.id, voucherItem};
            }
        }

        return null;
    }, [
        voucherPool,
        bundle,
        courseId,
        selectedPrice,
        retakeInsurancePrice,
        gettext,
        interpolate,
        examInTopic.issuerName,
        examInTopic.retakeInsurancePool,
    ]);

    // otherCourse.id is a string in CourseForCartUpsell; both cert and generic paths
    // use the same hydrated course, so a single parse covers both.
    const suggestedCourseId = otherCourse ? parseInt(otherCourse.id, 10) || -1 : -1;

    const shouldFetchPrice = suggestedCourseId >= 0 && !cartCourseIds?.has(suggestedCourseId);
    const {price: coursePrice, isLoading: coursePriceLoading} = useCoursePrice(
        shouldFetchPrice ? suggestedCourseId : null,
    );

    const otherCourseItem = useMemo(() => {
        // No qualifying bundle (no bundle, exam still loading, or generic suppressed by FF).
        if (!bundleType) return null;
        // Cert path normally requires a priced voucher before surfacing the other course.
        // When the voucher is already in cart, still surface remaining bundle courses.
        const canShowBundledCourse =
            bundleType === 'generic' || voucherAlreadyInCart || Boolean(resolved);
        if (bundleType === 'certification' && !canShowBundledCourse) return null;
        if (otherCourseLoading || !otherCourse || otherCourse.isEnrolled) return null;
        if (cartCourseIds?.has(suggestedCourseId)) return null;
        const candidate = buildShoppingItemFromGqlCourse(otherCourse, coursePrice, gettext);
        if (candidate && isEffectivelyFree(candidate, coursePriceLoading)) return null;
        return candidate;
    }, [
        bundleType,
        voucherAlreadyInCart,
        resolved,
        cartCourseIds,
        suggestedCourseId,
        otherCourseLoading,
        otherCourse,
        coursePrice,
        coursePriceLoading,
        gettext,
    ]);

    const showSuggestedCourse = !!otherCourseItem;

    const voucherOfferId =
        (resolved?.voucherItem.buyable as CertificationVoucherBuyable | undefined)?.offer_id ??
        voucherExamId;
    const showVoucher = !!resolved && !voucherAlreadyInCart;

    const isCartCourseFree = item.list_price.amount === 0;
    const partnerInCart = suggestedCourseId >= 0 && Boolean(cartCourseIds?.has(suggestedCourseId));
    const partnerEnrolled = Boolean(otherCourse?.isEnrolled);
    // Don't block cascade settlement on partner course/price fetches when we won't show them.
    const needsPartnerResolution =
        shouldFetchPrice && !partnerInCart && !partnerEnrolled && Boolean(otherCourse);
    const needsExamByTopic = Boolean(relatedTopicId) && !voucherAlreadyInCart;
    const isLoading =
        bundleLoading ||
        (needsExamByTopic && examIsLoading) ||
        (Boolean(relatedTopicId) && cartExamsLoading && !voucherInCartForBundleTopic) ||
        (needsPartnerResolution && (coursePriceLoading || otherCourseLoading));

    // Generic bundles have no voucher — hasContent only requires a renderable course.
    // Cert bundles require a priced voucher offer unless the voucher is already in cart.
    const hasContent =
        !isCartCourseFree &&
        !isLoading &&
        (bundleType === 'generic' || voucherAlreadyInCart
            ? showSuggestedCourse
            : Boolean(resolved) && (showSuggestedCourse || showVoucher));

    const wouldShowGenericBundle = useMemo(() => {
        if (rawBundleType !== 'generic') return false;
        if (isCartCourseFree) return false;
        if (otherCourseLoading || !otherCourse || otherCourse.isEnrolled) return false;
        if (cartCourseIds?.has(suggestedCourseId)) return false;
        const candidate = buildShoppingItemFromGqlCourse(otherCourse, coursePrice, gettext);
        if (!candidate || isEffectivelyFree(candidate, coursePriceLoading)) return false;
        return true;
    }, [
        rawBundleType,
        isCartCourseFree,
        otherCourseLoading,
        otherCourse,
        cartCourseIds,
        suggestedCourseId,
        coursePrice,
        coursePriceLoading,
        gettext,
    ]);

    return {
        bundleType,
        resolved,
        otherCourseItem,
        showSuggestedCourse,
        showVoucher,
        hasContent,
        voucherOfferId,
        isLoading,
        wouldShowGenericBundle,
    };
}
